import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { articlesAPI } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import toast from 'react-hot-toast';
import { Eye, X } from 'lucide-react';

const AdminArticleForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: 'career-tips',
    tags: [''],
    featuredImage: '',
    status: 'draft',
  });

  useEffect(() => { if (id) fetchArticle(); }, [id]);

  const fetchArticle = async () => {
    try {
      const res = await articlesAPI.getById(id!);
      const data = res.data.data;
      setFormData({ ...data, tags: data.tags?.length ? data.tags : [''] });
    } catch {
      toast.error('Failed to load article');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = { ...formData, tags: formData.tags.filter(t => t.trim()) };
      if (id) {
        await articlesAPI.update(id, data);
        toast.success('Article updated');
      } else {
        await articlesAPI.create(data);
        toast.success('Article created');
      }
      navigate('/admin/articles');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save article');
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => setFormData({ ...formData, tags: [...formData.tags, ''] });
  const removeTag = (index: number) => setFormData({ ...formData, tags: formData.tags.filter((_, i) => i !== index) });
  const updateTag = (index: number, value: string) => {
    const updated = [...formData.tags];
    updated[index] = value;
    setFormData({ ...formData, tags: updated });
  };

  const categoryLabels: Record<string, string> = {
    'career-tips': 'Career Tips', 'tech-news': 'Tech News', 'success-stories': 'Success Stories',
    'fraud-awareness': 'Fraud Awareness', 'skills-development': 'Skills Development', 'other': 'Other',
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">{id ? 'Edit' : 'Create'} Article</h1>
          <button type="button" onClick={() => setPreview(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium">
            <Eye className="w-4 h-4" /> Preview
          </button>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Title *</label>
            <input type="text" required value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <select value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                {Object.entries(categoryLabels).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Excerpt</label>
            <textarea rows={3} value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Brief summary of the article" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Content *</label>
            <textarea required rows={12} value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Featured Image URL</label>
            <input type="url" value={formData.featuredImage}
              onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="https://example.com/image.jpg" />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Tags</label>
            {formData.tags.map((tag, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input type="text" value={tag} onChange={(e) => updateTag(index, e.target.value)}
                  className="flex-1 px-4 py-2 border rounded-lg" placeholder="e.g., career, education" />
                <button type="button" onClick={() => removeTag(index)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg">Remove</button>
              </div>
            ))}
            <button type="button" onClick={addTag} className="text-blue-600 text-sm hover:underline">+ Add Tag</button>
          </div>

          <div className="flex gap-4">
            <button type="submit" disabled={loading}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Saving...' : id ? 'Update Article' : 'Save Article'}
            </button>
            <button type="button" onClick={() => navigate('/admin/articles')}
              className="bg-gray-200 px-6 py-2 rounded-lg hover:bg-gray-300">Cancel</button>
          </div>
        </form>
      </div>

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold">Preview</h2>
              <button onClick={() => setPreview(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {formData.featuredImage && (
                <img src={formData.featuredImage} alt="Featured" className="w-full h-56 object-cover rounded-lg" />
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                  {categoryLabels[formData.category]}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${formData.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {formData.status}
                </span>
                {formData.tags.filter(t => t.trim()).map((tag, i) => (
                  <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">{tag}</span>
                ))}
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{formData.title || 'Untitled'}</h1>
              {formData.excerpt && <p className="text-gray-500 italic border-l-4 border-blue-300 pl-4">{formData.excerpt}</p>}
              <div className="prose max-w-none text-gray-700 whitespace-pre-wrap">{formData.content}</div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminArticleForm;
